// Pure helpers turning a /partner/portfolio/ response into the shapes the
// league table and comparison overlay render. Kept separate from the
// components so the ranking/sorting/colour logic is unit-testable in
// isolation from React Query and MUI.

// Categorical palette (validated for CVD-safe adjacent contrast), light/dark
// steps per the dataviz palette reference. Capped at 8 fixed slots per the
// "never generate a 9th hue" rule; portfolios beyond 8 orgs share the muted
// fallback in the chart overlay (the three league tables below are the
// authoritative, uncapped ranking view).
const CATEGORICAL_LIGHT = [
    '#2a78d6',
    '#1baf7a',
    '#eda100',
    '#008300',
    '#4a3aa7',
    '#e34948',
    '#e87ba4',
    '#eb6834'
]
const CATEGORICAL_DARK = [
    '#3987e5',
    '#199e70',
    '#c98500',
    '#008300',
    '#9085e9',
    '#e66767',
    '#d55181',
    '#d95926'
]
const MUTED_FALLBACK = '#898781'

/**
 * Percent change of `value` vs `previousValue`, mirroring
 * MonthlyComparisonGrid's guard exactly: only defined when both periods have
 * a positive figure, so a missing/zero prior baseline never divides by zero
 * or implies a misleading +/-100% swing.
 */
export function computePercentChange(value, previousValue) {
    if (
        value === null ||
        value === undefined ||
        previousValue === null ||
        previousValue === undefined
    ) {
        return null
    }

    if (previousValue > 0 && value > 0) {
        return ((value - previousValue) / previousValue) * 100
    }

    return null
}

/**
 * Rows for one league table (one metric), sorted by rank ascending with
 * unranked orgs (missing metadata) pushed to the bottom rather than dropped —
 * they stay visible with an "not available" value and no rank/trend, per the
 * missing-metadata requirement.
 *
 * `idKey` / `nameKey` / `shortNameKey` name the identity fields on each item.
 * They default to the partner portfolio's `org_*` keys; the intra-org property
 * league passes `location_id` / `location_name` (property-league-and-house-
 * standings). The output keys (`orgId`, `orgName`, `orgShortName`) are the same
 * either way, so the partner path is unchanged.
 */
export function buildLeagueRows(
    orgs,
    {
        metricKey,
        previousMetricKey,
        rankGroup,
        idKey = 'org_id',
        nameKey = 'org_name',
        shortNameKey = 'org_short_name'
    }
) {
    const rows = (orgs || []).map((org) => {
        const value = org[metricKey]
        const previousValue = org[previousMetricKey]
        const rank = org.ranks?.[rankGroup]?.rank ?? null
        const previousRank = org.ranks?.[rankGroup]?.previous_rank ?? null
        const available = value !== null && value !== undefined

        return {
            orgId: org[idKey],
            orgName: org[nameKey],
            orgShortName: org[shortNameKey],
            buildingType: org.building_type,
            value,
            previousValue,
            rank,
            previousRank,
            available,
            percentChange: available
                ? computePercentChange(value, previousValue)
                : null
        }
    })

    return rows.sort((a, b) => {
        if (a.rank === null && b.rank === null) {
            return a.orgName.localeCompare(b.orgName)
        }
        if (a.rank === null) return 1
        if (b.rank === null) return -1
        return a.rank - b.rank
    })
}

/**
 * Two-category (Previous period / This period) dataset keyed by org name, the
 * shape `useLocationVisibilities` / `EchartsVerticalBar` already expect from
 * PowerComparisonPage — org rows standing in for house rows since the
 * portfolio endpoint has no per-org daily time series to overlay.
 */
export function buildComparisonChartData(orgs) {
    if (!Array.isArray(orgs) || orgs.length === 0) {
        return []
    }

    const previousRow = { name: 'Previous period' }
    const currentRow = { name: 'This period' }

    orgs.forEach((org) => {
        previousRow[org.org_name] = org.previous_total_kwh
        currentRow[org.org_name] = org.total_kwh
    })

    return [previousRow, currentRow]
}

/**
 * Fixed-order categorical series metadata for the comparison overlay, so
 * EchartsVerticalBar doesn't fall back to `useLocations`' isamsName colour
 * map (which has nothing to match org names against).
 */
export function buildOrgSeriesMetadata(orgs, locationVisibilities, isDarkMode) {
    const palette = isDarkMode ? CATEGORICAL_DARK : CATEGORICAL_LIGHT
    const visibilityMap = new Map(
        (locationVisibilities || []).map((location) => [
            location.name,
            location.visible
        ])
    )

    return (orgs || []).map((org, index) => ({
        name: org.org_name,
        color: palette[index] ?? MUTED_FALLBACK,
        visible: visibilityMap.get(org.org_name) ?? true
    }))
}
